import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { filterRequest } from "../../utilities/apiManager";
import axiosInstance from "../../utilities/axiosInstance";

let initialState = {
  isLoading: false,
  data: [],
  itemsCount: 0,
  pages: 0,
  error: null,
  singleRent: null,
  claims: [],
  claimsLoading: false,
  claimsError: null,
}

export const getRents = createAsyncThunk(
  "rents/getRents",
  async (args, thunkApi) => {
    try {
      const { page, size = 10, query = "", owner, user, filter = {} } = args;
      const filterStr = filterRequest(filter);
      const params = new URLSearchParams();

      // Only add non-empty params
      if (page) params.append("page", page);
      if (size) params.append("size", size);
      if (query) params.append("search", query);
      if (owner) params.append("owner", owner);
      if (user) params.append("user", user);

      const url = `/rents${params.toString() ? '?' + params.toString() : ''}${filterStr}`;

      const response = await axiosInstance.get(url);
      const { data: rentsArray, itemsCount, pages } = response.data.data;
      return thunkApi.fulfillWithValue({ data: rentsArray, itemsCount, pages });
    } catch (errors) {
      console.log(errors);
      return thunkApi.rejectWithValue(errors.response.data);
    }
  }
);

export const getRentsRequests = createAsyncThunk(
  "rents/getRentsRequests",
  async (args, thunkApi) => {
    try {
      const { page, size = 10, query = "", owner, user, filter = {} } = args;
      const filterStr = filterRequest(filter);
      const params = new URLSearchParams();

      // Only add non-empty params
      if (page) params.append("page", page);
      if (size) params.append("size", size);
      if (query) params.append("query", query);
      if (owner) params.append("owner", owner);
      if (user) params.append("user", user);

      const url = `/requests/rents${params.toString() ? '?' + params.toString() : ''}${filterStr}`;

      const response = await axiosInstance.get(url);
      const { data: rentsArray, itemsCount, pages } = response.data.data;
      return thunkApi.fulfillWithValue({ data: rentsArray, itemsCount, pages });
    } catch (errors) {
      console.log(errors);
      return thunkApi.rejectWithValue(errors.response.data);
    }
  }
);

export const getRent = createAsyncThunk(
  "rents/getRent",
  async (rentId, thunkApi) => {
    try {
      const { data } = await axiosInstance.get(`/rents/${rentId}`);
      return thunkApi.fulfillWithValue(data.data);
    } catch (errors) {
      console.log(errors);
      return thunkApi.rejectWithValue(errors.response.data);
    }
  }
);

export const getClaims = createAsyncThunk(
  "rents/getClaims",
  async (args, thunkApi) => {
    try {
      const { rentId, page = 1, size = 10 } = args;
      const params = new URLSearchParams();
      params.append("page", page);
      params.append("size", size);
      params.append("rent", rentId);

      const { data } = await axiosInstance.get(`/claims?${params.toString()}`);
      return thunkApi.fulfillWithValue(data.data);
    } catch (errors) {
      console.log(errors);
      return thunkApi.rejectWithValue(errors.response.data);
    }
  }
);

const rentsSlice = createSlice({
  name: "rents",
  initialState,
  extraReducers: (builder) => {
    builder
      .addCase(getRents.pending, (state) => {
        state.isLoading = true;
        state.error = null
      })
      .addCase(getRents.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        state.error = null;
        state.data = payload.data;
        state.itemsCount = payload.itemsCount;
        state.pages = payload.pages;
      })
      .addCase(getRents.rejected, (state, { payload }) => {
        state.isLoading = false;
        state.error = payload?.message;
      })
      .addCase(getRentsRequests.pending, (state) => {
        state.isLoading = true;
        state.error = null
      })
      .addCase(getRentsRequests.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        state.error = null;
        state.data = payload.data;
        state.itemsCount = payload.itemsCount;
        state.pages = payload.pages;
      })
      .addCase(getRentsRequests.rejected, (state, { payload }) => {
        state.isLoading = false;
        state.error = payload?.message;
      })
      .addCase(getRent.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getRent.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        state.error = null;
        state.singleRent = payload;
      })
      .addCase(getRent.rejected, (state, { payload }) => {
        state.isLoading = false;
        state.error = payload?.message;
      })
      .addCase(getClaims.pending, (state) => {
        state.claimsLoading = true;
        state.claimsError = null;
      })
      .addCase(getClaims.fulfilled, (state, { payload }) => {
        state.claimsLoading = false;
        state.claimsError = null;
        state.claims = payload;
      })
      .addCase(getClaims.rejected, (state, { payload }) => {
        state.claimsLoading = false;
        state.claimsError = payload?.message;
      })
  }
});

export default rentsSlice.reducer;